/**
 * Spec FR1. First name, last name, email, explicit consent.
 *
 * Field errors come from the API rather than being duplicated here, so the
 * server's rules are the only rules. The one client-side check is `required`,
 * which stops a pointless round trip on an empty form.
 *
 * Anything other than a field error, a lost connection, a 429, a server fault,
 * is handed to the parent, which shows the matching state screen with a retry
 * that re-submits this form. The form stays mounted, so nothing typed is lost.
 */

import { useId, useState, type FormEvent } from 'react';
import { RESPONDENT_INSTRUCTION, TOTAL_QUESTIONS } from '@dna17q/shared';
import { ApiRequestError, createParticipant } from '../api.ts';

interface Props {
  source: string | null;
  onStarted: (responseId: string, firstName: string) => void;
  onFailure: (err: unknown, retry: () => void) => void;
}

export function Landing({ source, onStarted, onFailure }: Props) {
  const ids = { first: useId(), last: useId(), email: useId(), consent: useId() };
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (submitting) return;

    setSubmitting(true);
    setFields({});

    try {
      const created = await createParticipant({
        first_name: firstName,
        last_name: lastName,
        email,
        consent,
        source,
      });
      onStarted(created.response_id, firstName.trim());
    } catch (err) {
      setSubmitting(false);
      if (err instanceof ApiRequestError && err.body.error === 'validation_failed' && err.body.fields) {
        setFields(err.body.fields);
        return;
      }
      onFailure(err, () => void submit());
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void submit();
  }

  return (
    <form className="card" onSubmit={onSubmit} noValidate>
      <h1>DNA Behavior Discovery</h1>
      <p className="lede">
        {TOTAL_QUESTIONS} quick questions, about five minutes. {RESPONDENT_INSTRUCTION}
      </p>

      <div className="field">
        <label htmlFor={ids.first}>First name</label>
        <input
          id={ids.first}
          name="first_name"
          type="text"
          autoComplete="given-name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          aria-invalid={fields['first_name'] ? true : undefined}
          aria-describedby={fields['first_name'] ? `${ids.first}-error` : undefined}
          required
        />
        {fields['first_name'] ? (
          <p className="field-error" id={`${ids.first}-error`}>
            {fields['first_name']}
          </p>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor={ids.last}>Last name</label>
        <input
          id={ids.last}
          name="last_name"
          type="text"
          autoComplete="family-name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          aria-invalid={fields['last_name'] ? true : undefined}
          aria-describedby={fields['last_name'] ? `${ids.last}-error` : undefined}
          required
        />
        {fields['last_name'] ? (
          <p className="field-error" id={`${ids.last}-error`}>
            {fields['last_name']}
          </p>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor={ids.email}>Email</label>
        <input
          id={ids.email}
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={fields['email'] ? true : undefined}
          aria-describedby={fields['email'] ? `${ids.email}-error` : undefined}
          required
        />
        {fields['email'] ? (
          <p className="field-error" id={`${ids.email}-error`}>
            {fields['email']}
          </p>
        ) : null}
      </div>

      <div className="consent">
        <input
          id={ids.consent}
          name="consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          aria-invalid={fields['consent'] ? true : undefined}
          aria-describedby={fields['consent'] ? `${ids.consent}-error` : undefined}
        />
        <label htmlFor={ids.consent}>
          I agree to DNA Behavior storing my responses and contact details so that my results can be
          prepared and discussed at the session.
        </label>
      </div>
      {fields['consent'] ? (
        <p className="field-error" id={`${ids.consent}-error`} style={{ marginBottom: 16 }}>
          {fields['consent']}
        </p>
      ) : null}

      <button className="btn btn-primary" type="submit" disabled={submitting}>
        {submitting ? 'Starting...' : 'Start'}
      </button>
    </form>
  );
}
